import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  Collapse,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useParams, useNavigate } from 'react-router-dom';
import SyncRounded from '@mui/icons-material/SyncRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import PendingRounded from '@mui/icons-material/PendingRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import { buildApiUrl } from '../config';
import { SnackbarContext } from '../ui/SnackbarProvider';
import { markProductStep } from '../hooks/useProductOnboarding';
import { SyncJobContext } from '../ui/SyncJobProvider';
import { formatRelativeTime } from '../utils/date';
import {
  SyncJob,
  Step,
  SubStep,
  SubSubStep,
  StepStatus,
  StatusChip,
  parseUtcDate,
  formatDuration,
} from './DataSyncPage';

// ---------------------------------------------------------------------------
// Status icon
// ---------------------------------------------------------------------------

function StatusIcon({ status, size = 18 }: { status: StepStatus; size?: number }) {
  const theme = useTheme();
  if (status === 'completed') return <CheckCircleRounded sx={{ fontSize: size, color: theme.palette.success.main }} />;
  if (status === 'failed')    return <ErrorRounded sx={{ fontSize: size, color: theme.palette.error.main }} />;
  if (status === 'running')   return <CircularProgress size={size - 2} thickness={5} />;
  return <PendingRounded sx={{ fontSize: size, color: theme.palette.text.disabled }} />;
}

// ---------------------------------------------------------------------------
// Step icon + label normalisation (backend still sends old names)
// ---------------------------------------------------------------------------

function stepIcon(stepId: string, size = 18) {
  if (stepId === 'refresh_schema') return <RefreshRounded sx={{ fontSize: size }} />;
  if (stepId === 'profiling')      return <TableChartRounded sx={{ fontSize: size }} />;
  if (stepId === 'build_ontology') return <AccountTreeRounded sx={{ fontSize: size }} />;
  return <StorageRounded sx={{ fontSize: size }} />;
}

function normaliseStepLabel(label: string): string {
  if (label === 'Build Data Relationships') return 'Build Data Model';
  return label;
}

const STEP_DESCRIPTIONS: Record<string, string> = {
  refresh_schema:  'Fetches the latest table and column structure from each connector',
  profiling:       'Computes statistics and data quality metrics for selected tables',
  build_ontology:  'AI discovers entities, maps fields, and generates relationships',
};

function stepDescription(stepId: string): string | null {
  return STEP_DESCRIPTIONS[stepId] ?? null;
}

// ---------------------------------------------------------------------------
// Sub-substep row (e.g. individual table inside profiling)
// ---------------------------------------------------------------------------

const SubSubStepRow: React.FC<{ subStep: SubSubStep }> = ({ subStep }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: { xs: 2, sm: 4 }, py: 0.25, minWidth: 0 }}>
      <StatusIcon status={subStep.status} size={14} />
      <Typography variant="caption" noWrap title={subStep.label} sx={{ color: theme.palette.text.secondary, flex: '1 1 0%', minWidth: 0 }}>
        {subStep.label}
      </Typography>
      {subStep.error && (
        <Tooltip title={subStep.error} placement="top">
          <Typography variant="caption" color="error"
            sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subStep.error}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Sub-step row (connector within refresh/profiling, or phase within ontology)
// ---------------------------------------------------------------------------

const SubStepRow: React.FC<{ ss: SubStep; defaultExpanded?: boolean }> = ({ ss, defaultExpanded = false }) => {
  const theme = useTheme();
  const hasChildren = (ss.substeps || []).length > 0;
  const [open, setOpen] = React.useState(defaultExpanded);

  React.useEffect(() => {
    if (ss.status === 'running') setOpen(true);
  }, [ss.status]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          pl: { xs: 1, sm: 2 }, py: 0.4, borderRadius: 1,
          minWidth: 0,
          cursor: hasChildren ? 'pointer' : 'default',
          '&:hover': hasChildren ? { bgcolor: theme.palette.action.hover } : {},
        }}
        onClick={() => hasChildren && setOpen((v: boolean) => !v)}
      >
        <StatusIcon status={ss.status} size={16} />
        <Typography variant="body2" noWrap title={ss.label} sx={{ flex: '1 1 0%', minWidth: 0, fontWeight: ss.status === 'running' ? 600 : 400 }}>
          {ss.label}
        </Typography>
        {ss.error && ss.status === 'failed' && (
          <Tooltip title={ss.error} placement="top">
            <Typography variant="caption" color="error"
              sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ss.error}
            </Typography>
          </Tooltip>
        )}
        {hasChildren && (
          <IconButton size="small" sx={{ p: 0.25 }}>
            {open ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
          </IconButton>
        )}
      </Box>
      {hasChildren && (
        <Collapse in={open} unmountOnExit>
          {(ss.substeps || []).map((subStep) => (
            <SubSubStepRow key={subStep.id} subStep={subStep} />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Top-level step accordion
// ---------------------------------------------------------------------------

const StepAccordion: React.FC<{ step: Step }> = ({ step }) => {
  const theme = useTheme();
  const hasChildren = (step.substeps || []).length > 0;
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (step.status === 'running') setOpen(true);
  }, [step.status]);

  const borderColor =
    step.status === 'completed' ? theme.palette.success.main :
    step.status === 'failed'    ? theme.palette.error.main   :
    step.status === 'running'   ? theme.palette.primary.main :
    theme.palette.divider;

  return (
    <Paper variant="outlined" sx={{ borderColor, borderRadius: 2, overflow: 'hidden', transition: 'border-color 0.3s' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          rowGap: 1,
          px: { xs: 1.5, sm: 2 }, py: 1.25,
          cursor: hasChildren ? 'pointer' : 'default',
          bgcolor: step.status === 'running'
            ? (theme.palette.mode === 'dark' ? 'rgba(25,118,210,0.08)' : 'rgba(25,118,210,0.04)')
            : 'transparent',
          '&:hover': hasChildren ? { bgcolor: theme.palette.action.hover } : {},
        }}
        onClick={() => hasChildren && setOpen((v: boolean) => !v)}
      >
        <Box sx={{ color: theme.palette.text.secondary, display: 'flex', flexShrink: 0 }}>
          {stepIcon(step.id)}
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{normaliseStepLabel(step.label)}</Typography>
          {stepDescription(step.id) && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3, mt: 0.15 }}>
              {stepDescription(step.id)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: { xs: 'auto', sm: 0 } }}>
          <StatusIcon status={step.status} size={20} />
          {step.error && step.status === 'failed' && (
            <Tooltip title={step.error} placement="top">
              <Typography variant="caption" color="error"
                sx={{ maxWidth: { xs: 120, sm: 240 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {step.error}
              </Typography>
            </Tooltip>
          )}
          {hasChildren && (
            <IconButton size="small" sx={{ p: 0.25 }}>
              {open ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>
      {hasChildren && (
        <Collapse in={open} unmountOnExit>
          <Divider />
          <Box sx={{ py: 0.75, px: { xs: 0.5, sm: 1 } }}>
            {(step.substeps || []).map(ss => (
              <SubStepRow key={ss.id} ss={ss} defaultExpanded={step.id === 'profiling'} />
            ))}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Job detail page
// ---------------------------------------------------------------------------

export default function DataSyncJobPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { notify } = React.useContext(SnackbarContext);
  const { notifyJobUpdate } = React.useContext(SyncJobContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tenantId, setTenantId] = React.useState<string | null>(null);
  const [job, setJob] = React.useState<SyncJob | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0);
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  // Track whether the job was actively running when we started polling,
  // so we only toast on live transitions - not when loading a historical job.
  const wasLiveRef = React.useRef(false);
  const isFirstFetchRef = React.useRef(true);

  const authHeader = React.useCallback(() => {
    const token = localStorage.getItem('klaris_jwt');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Load tenant id
  React.useEffect(() => {
    const raw = localStorage.getItem('klaris_tenant');
    if (!raw) return;
    try {
      const t = JSON.parse(raw);
      setTenantId(t.tenant_id || t.id || null);
    } catch { setTenantId(raw); }
  }, []);

  // Fetch job
  const fetchJob = React.useCallback(async () => {
    if (!tenantId || !jobId) return;
    try {
      const r = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/sync-jobs/${jobId}`), {
        headers: authHeader() as HeadersInit,
      });
      if (!r.ok) { setNotFound(true); return; }
      const data: SyncJob = await r.json();
      setJob(data);

      const isFirst = isFirstFetchRef.current;
      isFirstFetchRef.current = false;

      // Mark as live only if the job was running/pending on first load
      if (isFirst && (data.status === 'running' || data.status === 'pending')) {
        wasLiveRef.current = true;
        // Immediately surface the job in the global banner without waiting for its next poll
        notifyJobUpdate(data as any);
      }

      if (data.status === 'completed' || data.status === 'failed') {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        // Clear the global banner immediately - don't wait for the provider's next poll
        notifyJobUpdate(null);
        // Only show toast for transitions we observed live, not historical loads
        if (wasLiveRef.current) {
          if (data.status === 'completed') {
            notify('Data sync completed successfully!', 'success');
            markProductStep('data_model_built');
            // Self-serve onboarding: user stayed on this page while sync ran - go to Copilot for the next step.
            let selfServe = false;
            try {
              const raw = localStorage.getItem('klaris_tenant');
              if (raw) selfServe = JSON.parse(raw).onboarding_source === 'self_serve';
            } catch { /* ignore */ }
            if (selfServe) {
              navigate('/copilot', { replace: true });
            }
          }
          else notify(`Sync failed: ${data.error_message || 'Unknown error'}`, 'error');
          wasLiveRef.current = false;
        }
      }
    } catch (e) {
      console.error('Failed to fetch job', e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, jobId, authHeader, notify, notifyJobUpdate, navigate]);

  React.useEffect(() => {
    if (!tenantId || !jobId) return;
    fetchJob();
  }, [fetchJob]);

  // Start polling when job is running/pending
  React.useEffect(() => {
    if (!job) return;
    if (job.status === 'running' || job.status === 'pending') {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchJob, 5000);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [job?.status, fetchJob]);

  // Cleanup on unmount
  React.useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Live elapsed timer
  React.useEffect(() => {
    if (!job || (job.status !== 'running' && job.status !== 'pending')) return;
    const start = parseUtcDate(job.created_at).getTime();
    const tick = () => setElapsed(Date.now() - start);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [job?.status, job?.created_at]);

  const finalDuration = React.useMemo(() => {
    if (!job?.completed_at) return null;
    const ms = parseUtcDate(job.completed_at).getTime() - parseUtcDate(job.created_at).getTime();
    return formatDuration(ms);
  }, [job?.completed_at, job?.created_at]);

  // ---------------------------------------------------------------------------
  // Render states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound || !job) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>Sync job not found.</Alert>
        <Button startIcon={<ArrowBackRounded />} onClick={() => navigate('/data-sync')}>
          Back to Data Sync
        </Button>
      </Box>
    );
  }

  const steps: Step[] = job.progress?.steps || [];

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: { xs: 2, md: 0 } }}>
      {/* Page header */}
      <Box sx={(t) => ({ mb: 2, pb: 1.5, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 2, md: 0 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{ flexWrap: 'wrap', gap: 1, rowGap: 0.75 }}
          >
            <Button
              size="small"
              variant="text"
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate('/data-sync')}
              sx={{ color: 'text.secondary' }}
            >
              Data Sync
            </Button>
            <StatusChip status={job.status} />
            {(job.status === 'running' || job.status === 'pending') && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                {formatDuration(elapsed)} elapsed
              </Typography>
            )}
            {finalDuration && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                took {finalDuration}
              </Typography>
            )}
            {job.completed_at && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                · {formatRelativeTime(job.completed_at)}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
              · {job.connector_ids.length} connector{job.connector_ids.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>

          {(job.status === 'completed' || job.status === 'failed') && (
            <Button
              variant="outlined"
              startIcon={<SyncRounded />}
              size="small"
              fullWidth={isMobile}
              onClick={() => navigate('/data-sync')}
              sx={{ flexShrink: 0 }}
            >
              Start New Sync
            </Button>
          )}
        </Stack>
      </Box>

      {/* Alert banners */}
      {job.status === 'failed' && job.error_message && (
        <Alert severity="error" sx={{ mb: 2 }}>{job.error_message}</Alert>
      )}
      {job.status === 'completed' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Sync completed successfully. Your schema, profiles, and data model are up to date.
        </Alert>
      )}

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2.5, md: 3 }, alignItems: 'stretch' }}>
        <Stack spacing={1.5} sx={{ flex: '1 1 0', minWidth: 0, order: { xs: 1, md: 1 } }}>
          {steps.map(step => <StepAccordion key={step.id} step={step} />)}
        </Stack>

        {/* Right: summary card */}
        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, order: { xs: 2, md: 2 } }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 1.75, sm: 2 } }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem', display: 'block', mb: 1.5 }}>
              Sync Summary
            </Typography>
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.25 }}><StatusChip status={job.status} /></Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Connectors</Typography>
                <Typography variant="body2" fontWeight={500}>{job.connector_ids.length}</Typography>
              </Box>
              {finalDuration && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Duration</Typography>
                  <Typography variant="body2" fontWeight={500}>{finalDuration}</Typography>
                </Box>
              )}
              {job.completed_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Completed</Typography>
                  <Typography variant="body2" fontWeight={500}>{formatRelativeTime(job.completed_at)}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Started</Typography>
                <Typography variant="body2" fontWeight={500}>{formatRelativeTime(job.created_at)}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
